import AuthService from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/services/AuthService";
import { useLazoUserLanguage } from "@/hooks/queries/lazo-user/useLazoUserQueries";
import React, { createContext, useContext, useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";

interface LanguageContextType {
  language: string;
  isLoading: boolean;
  setLanguage: (lang: string) => void;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

interface LanguageProviderProps {
  children: React.ReactNode;
}

const DEFAULT_LANGUAGE = "en";

export function LanguageProvider({ children }: LanguageProviderProps) {
  const { i18n } = useTranslation();

  const loggedUser = AuthService.getUser();
  const { data: userLanguage, isLoading } = useLazoUserLanguage(loggedUser?.externalId);

  const prevUserLanguage = useRef<string | undefined>(undefined);

  // Backend is the source of truth - use it directly, fallback to default
  const language = userLanguage || DEFAULT_LANGUAGE;

  useEffect(() => {
    // Update i18n when backend language changes
    if (userLanguage && userLanguage !== prevUserLanguage.current) {
      prevUserLanguage.current = userLanguage;
      i18n.changeLanguage(userLanguage);
    }
  }, [userLanguage, i18n]);

  // Function to change language (updates i18n immediately, backend is updated by caller)
  const setLanguage = (newLang: string) => {
    i18n.changeLanguage(newLang);
  };

  const value = useMemo(() => ({ language, isLoading, setLanguage }), [language, isLoading]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguageContext() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguageContext must be used within a LanguageProvider");
  }
  return context;
}
